import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { LogOut, Truck, Package, User, ShieldCheck } from "lucide-react";
import NotificationsBell from "./NotificationsBell";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isActive = (p) => loc.pathname.startsWith(p);

  /** Tabs harmonisées — UNIQUEMENT vert forêt #1F4032 + blanc.
   *  Inactive : bord transparent, texte vert dilué, fond transparent.
   *  Active   : fond vert plein + texte blanc + ombre subtile.
   */
  const tabClass = (active) => [
    "rounded-full gap-2 h-10 sm:h-11 px-3 sm:px-4 text-sm font-semibold transition-all border inline-flex items-center",
    active
      ? "bg-gradient-to-br from-[#1F4032] to-[#0F2A20] border-[#1F4032] text-white shadow-md shadow-[#1F4032]/25"
      : "bg-transparent border-transparent text-[#1F4032]/75 hover:bg-[#1F4032]/5 hover:text-[#1F4032] hover:border-[#1F4032]/15",
  ].join(" ");

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-[#1F4032]/10">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 h-[68px] flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-2 group shrink-0" data-testid="nav-logo">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#1F4032] to-[#0F2A20] flex items-center justify-center shadow-md shadow-[#1F4032]/25">
            <Truck className="w-[18px] h-[18px] text-white" strokeWidth={2.5} />
          </div>
          <span className="font-display font-bold text-xl tracking-tighter text-[#15302A]">MML</span>
          <span className="overline text-[#1F4032]/55 hidden lg:inline ml-2">Plateforme Logistique</span>
        </Link>

        <nav className="flex items-center gap-1 sm:gap-1.5">
          {!user && (
            <>
              <Link to="/login" data-testid="nav-login">
                <Button data-testid="nav-login-button" variant="ghost" className="rounded-full h-10 sm:h-11 px-2 sm:px-4 text-sm font-semibold text-[#1F4032] hover:bg-[#1F4032]/5 hover:text-[#0F2A20]">
                  Connexion
                </Button>
              </Link>
              <Link to="/register" data-testid="nav-register">
                <button
                  type="button"
                  data-testid="nav-register-button"
                  className="rounded-full h-10 sm:h-11 px-3 sm:px-5 text-sm font-semibold text-white bg-gradient-to-br from-[#1F4032] to-[#0F2A20] hover:from-[#2A5443] hover:to-[#1F4032] shadow-md shadow-[#1F4032]/25 hover:shadow-lg hover:shadow-[#1F4032]/35 transition-all"
                >
                  S&apos;inscrire
                </button>
              </Link>
            </>
          )}

          {user?.role === "shipper" && (
              <Link to="/shipper" data-testid="nav-shipper" title="Expéditeur" className="shrink-0">
                <button type="button" className={tabClass(isActive("/shipper"))}>
                  <Package className="w-[18px] h-[18px]" />
                  <span className="hidden xs:inline sm:inline">Expéditeur</span>
                  <span className="xs:hidden sm:hidden">Exp.</span>
                </button>
              </Link>
          )}
          {user?.role === "transporter" && (
              <Link to="/transporter" data-testid="nav-transporter" title="Transporteur" className="shrink-0">
                <button type="button" className={tabClass(isActive("/transporter") || isActive("/kyc"))}>
                  <Truck className="w-[18px] h-[18px]" />
                  <span className="hidden xs:inline sm:inline">Transporteur</span>
                  <span className="xs:hidden sm:hidden">Trans.</span>
                </button>
              </Link>
          )}

          {user && user.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin" title="Admin" className="shrink-0">
              <button type="button" className={tabClass(isActive("/admin"))}>
                <ShieldCheck className="w-[18px] h-[18px]" />
                Admin
              </button>
            </Link>
          )}

          {user && (
            <>
              <NotificationsBell />
              <Link
                to={`/u/${user.id}`}
                className="hidden md:flex items-center gap-2 px-3 ml-1 text-sm font-medium text-[#1F4032] border-l border-[#1F4032]/15 hover:text-[#0F2A20] transition-colors"
                data-testid="nav-my-profile"
                title="Mon profil"
              >
                <User className="w-4 h-4" />
                {user.name}
              </Link>
              <Link
                to={`/u/${user.id}`}
                className="md:hidden p-2.5 rounded-full hover:bg-[#1F4032]/5 transition-colors text-[#1F4032]"
                data-testid="nav-my-profile-mobile"
                title="Mon profil"
                aria-label="Mon profil"
              >
                <User className="w-[18px] h-[18px]" />
              </Link>
              <button
                type="button"
                className="rounded-full h-10 sm:h-11 px-3 sm:px-4 text-sm font-semibold gap-2 inline-flex items-center text-[#1F4032]/75 hover:text-[#0F2A20] hover:bg-[#1F4032]/5 transition-colors"
                data-testid="nav-logout"
                onClick={async () => { await logout(); nav("/"); }}
                title="Déconnexion"
                aria-label="Déconnexion"
              >
                <LogOut className="w-[18px] h-[18px]" />
                <span className="hidden sm:inline">Déconnexion</span>
              </button>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
